import DetailContext from '@shared/components/utils/context/detailContext';
import DetailSynopsisArrows from './detailSynopsisArrows';
import DetailSynopsisContainer from '@components/atoms/container/detailSynopsisContainer';
import DetailSynopsisSelectorContainer from '@components/atoms/container/detailSynopsisSelectorContainer';
import DetailSynopsisText from '@components/atoms/text/detailSynopsisText';
import Focus from '@shared/types/enums/focus';
import PaginationDots from '@components/elements/pagination/paginationDots';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { magicRemoteSelector } from '@shared/store/slices/magicRemote.slice';
import { focusSelector, setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';

const TEXT_HEIGHT = 160;

export default function DetailSynopsis() {
  const dispatch = useAppDispatch();
  const { focus } = useAppSelector(focusSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);
  const { asset } = useContext(DetailContext);
  const [offset, setOffset] = useState(0); // keep track of transform offset
  const containerRef = useRef(null);
  const textRef = useRef<HTMLDivElement>(null);
  const synopsisRef = useRef(asset?.synopsis);
  synopsisRef.current = asset?.synopsis;

  // Memoized data

  const { dotsLength, dotsIndex, isAhead, isOverflown, synopsis } =
    useMemo(() => {
      const dotsLength = textRef?.current
        ? Math.ceil(textRef.current?.clientHeight / TEXT_HEIGHT)
        : 0;
      const dotsIndex = Math.floor(Math.abs(offset) / TEXT_HEIGHT);
      const isAhead = offset !== 0;
      const isOverflown = textRef.current
        ? Math.abs(offset) + TEXT_HEIGHT < textRef.current.clientHeight
        : false;
      return {
        dotsLength,
        dotsIndex,
        isAhead,
        isOverflown,
        synopsis: asset?.synopsis,
      };
    }, [asset, offset, textRef.current?.clientHeight]);

  // Actions

  const navigateLeft = useCallback(() => {
    setOffset((offset) => {
      if (textRef.current) {
        const newOffset = Math.min(offset + TEXT_HEIGHT, 0);
        textRef.current.style.transform = `translateY(${newOffset}px)`;
        return newOffset;
      }
      return offset;
    });
  }, []);

  const navigateRight = useCallback(() => {
    setOffset((offset) => {
      if (textRef.current) {
        let newOffset = offset - TEXT_HEIGHT;
        if (newOffset <= -textRef.current.clientHeight) {
          newOffset = offset;
        }
        textRef.current.style.transform = `translateY(${newOffset}px)`;
        return newOffset;
      }
      return offset;
    });
  }, []);

  const onMouseEnter = useCallback(() => {
    if (synopsisRef.current?.length && synopsisRef.current?.length > 1) {
      dispatch(setFocus(Focus.DETAIL_SYNOPSIS));
    }
  }, []);

  // Keyboard hooks

  useKeyboard({
    focus: Focus.DETAIL_SYNOPSIS,
    keyDownHandler: () => null,
    keyUpHandler: (key) => {
      if (!textRef.current) return;
      if (!synopsisRef?.current?.length) {
        return;
      }
      switch (key) {
        case 'LEFT':
          navigateLeft();
          break;
        case 'RIGHT':
          navigateRight();
          break;
      }
    },
  });

  return synopsis?.length ? (
    <DetailSynopsisContainer
      ref={containerRef}
      id={'synopsisContainer'}
      onMouseEnter={onMouseEnter}
    >
      <DetailSynopsisText ref={textRef} id={'synopsisText'}>
        {synopsis}
      </DetailSynopsisText>
      <DetailSynopsisArrows
        display={{ left: isAhead, right: isOverflown }}
        focused={focus === Focus.DETAIL_SYNOPSIS || enabled}
        navigate={{ left: navigateLeft, right: navigateRight }}
      />
      <PaginationDots
        Container={DetailSynopsisSelectorContainer}
        index={dotsIndex}
        length={dotsLength}
      />
    </DetailSynopsisContainer>
  ) : null;
}
